import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { CorporatePartHelper } from 'corporate/helpers';
import { useCanManageParts, useUpdateCorporatePartPricingType } from 'corporate/hooks';
import { ICorporatePart } from 'corporate/types';
import { PricingType } from 'pricing/enums';
import { Color, Size, Variant } from 'theme/enums';

import { ChangeToManualDrawer } from './ChangeToManualDrawer';

interface IProps {
  corporatePart: Partial<ICorporatePart>;
}

export const EditPricingTypeButton = ({ corporatePart }: IProps) => {
  const { t } = useTranslation();
  const canManageParts = useCanManageParts();
  const { updatePricingType } = useUpdateCorporatePartPricingType(NotificationPosition.DRAWER);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const isSubmittingRef = useRef(false);

  const canEditPricingType =
    canManageParts && CorporatePartHelper.isSetUp(corporatePart) && !!corporatePart?.id;

  if (!canEditPricingType) {
    return null;
  }

  const isManual = corporatePart?.pricingType === PricingType.MANUAL;

  const onClickEdit = () => {
    if (isManual) {
      setIsConfirmOpen(true);
    } else {
      setIsDrawerOpen(true);
    }
  };

  const onConfirmChangeToCalculated = async () => {
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    setIsConfirmOpen(false);

    try {
      await updatePricingType({
        corporatePart: {
          id: corporatePart.id,
          pricingType: PricingType.CALCULATED,
        },
      });
    } finally {
      isSubmittingRef.current = false;
    }
  };

  return (
    <>
      <Button
        text={t('buttonLabelEdit')}
        variant={Variant.SOFT}
        color={Color.PRIMARY}
        size={Size.SM}
        onClick={onClickEdit}
      />

      {isConfirmOpen && (
        <Confirmation
          isOpen={isConfirmOpen}
          title={t('titleInfo')}
          message={t('messageChangePricingToCalculated')}
          onConfirm={onConfirmChangeToCalculated}
          onCancel={() => setIsConfirmOpen(false)}
        />
      )}

      {isDrawerOpen && (
        <ChangeToManualDrawer
          corporatePart={corporatePart}
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
        />
      )}
    </>
  );
};
